import { axiosInstance } from '@/http-common'

/**
 * Get asset groups with pagination and filtering for DevExtreme DataGrid
 * @param params - Query parameters (skip, take, searchValue, sort, filter)
 */
export async function getAssetGroups(params?: {
  skip?: number
  take?: number
  searchValue?: string
  sort?: any
  filter?: any
}): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/asset-groups', { params })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get asset groups with temperature/humidity averages.
 * @param params - Query parameters (skip, take, searchValue, sort, filter)
 */
export async function getAssetGroupsAverages(params?: {
  skip?: number
  take?: number
  searchValue?: string
  sort?: any
  filter?: any
}): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/asset-groups/averages', { params })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get asset groups tree hierarchy
 */
export async function getAssetGroupsTree(): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/asset-groups/tree')
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get single asset group by ID with full details (users, assets, etc.)
 * @param id - Asset group ID
 */
export async function getAssetGroup(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/asset-group/${id}`)
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get averages for a single asset group.
 * @param id - Asset group ID
 */
export async function getAssetGroupAverages(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/asset-group/${id}/averages`)
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get averaged history series for a single asset group.
 * @param id - Asset group ID
 * @param dateFrom - ISO string date from
 * @param dateTo - ISO string date to
 */
export async function getAssetGroupHistoryRange(
  id: number | string,
  dateFrom: string,
  dateTo: string
): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/asset-group/${id}/history`, {
      params: {
        date_from: dateFrom,
        date_to: dateTo
      }
    })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get assets for a group with calculated averages.
 * @param id - Asset group ID
 */
/**
 * Get asset groups with location data
 * @param params - Query parameters (skip, take, searchValue, sort, filter)
 */
export async function getAssetGroupsWithLocation(params?: {
  skip?: number
  take?: number
  searchValue?: string
  sort?: any
  filter?: any
}): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/asset-groups-with-location', { params })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Search users for asset group assignment
 * @param query - Search query
 * @param limit - Result limit
 */
export async function searchAssetGroupUsers(query: string, limit: number = 10): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get('/api/v1/asset-group/users/search', {
      params: { q: query, limit }
    })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Search vehicles for asset group assignment.
 * With hierarchical filtering: if groupId is provided, returns only vehicles from parent's pool.
 * @param query - Search query (registration or IMEI), use '*' to load all
 * @param limit - Result limit
 * @param groupId - Optional group ID for hierarchical filtering
 */
export async function searchAssetGroupVehicles(query: string, limit: number = 10, groupId?: number | null): Promise<any> {
  return new Promise((resolve, reject) => {
    const params: { q: string; limit: number; groupId?: number } = { q: query, limit }
    if (groupId) {
      params.groupId = groupId
    }
    axiosInstance.get('/api/v1/asset-group/vehicles/search', { params })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Search assets for asset group assignment
 * With hierarchical filtering: if groupId is provided, returns only assets from parent's pool
 * @param query - Search query
 * @param limit - Result limit
 * @param groupId - Optional group ID for hierarchical filtering (returns parent's assets pool)
 */
export async function searchAssetGroupAssets(query: string, limit: number = 10, groupId?: number | null): Promise<any> {
  return new Promise((resolve, reject) => {
    const params: { q: string; limit: number; groupId?: number } = { q: query, limit }
    if (groupId) {
      params.groupId = groupId
    }
    axiosInstance.get('/api/v1/asset-group/assets/search', { params })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Get asset group location details
 * @param id - Asset group ID
 */
export async function getAssetGroupLocation(id: number | string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.get(`/api/v1/asset-group/${id}/location`)
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}
